import {AfterViewInit, Component, ElementRef, OnInit, QueryList, Renderer2, ViewChildren} from '@angular/core';
import {CommandService} from '@app/services/command.service';
import {Store} from '@ngxs/store';
import {BlackboardService} from '@app/modules/blackboard/services/blackboard.service';
import {BlackboardState, BlackboardStateModel} from '@app/state/blackboard.state';
import {BbDrawInfo, BlackboardData, BlackboardTools, Coordinates} from '@app/modules/blackboard/model/blackboardInfo';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {AccessType, JsonLogAction, ViewportTab} from '@app/modules/shared/data/constants';
import {JsonLogService} from '@app/modules/shared/services/json-log.service';
import {Rect} from '@app/modules/svg/model/Rect';
import {Point} from '@app/modules/svg/model/Point';
import {UtilService} from '@app/modules/shared/services/util.service';
import {VcrClassroomComponent} from '@app/components/vcr-classroom/vcr-classroom.component';

@Component({
  selector: '[app-blackboard]',
  templateUrl: './blackboard.component.html',
  styleUrls: ['./blackboard.component.css']
})
export class BlackboardComponent implements OnInit, AfterViewInit {

  public localUser: LocalUser;
  public tools = BlackboardTools;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public AccessType = AccessType;
  public selectedTool = this.tools.DRAW_MODE;
  public text = '';
  public pointer = {
    isHidden: true
  };
  public blackboardStateModel: BlackboardStateModel;
  private canvas: HTMLCanvasElement;
  private pointerElement: SVGSVGElement;
  // @ViewChild('cursor') cursor: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private isPainting: boolean;
  private messageSendTimer;
  private bbInfo = new BbDrawInfo();
  private coordinates = {} as Coordinates;
  public isShiftDown: boolean = false;
  public isCtrlDown: boolean = false;
  private lastShiftPoint: Point = {x: this.coordinates.x, y: this.coordinates.y};
  private lastCtrlPoint: Point = {x: this.coordinates.x, y: this.coordinates.y};

  @ViewChildren('bbTextArea')
  public bbTextArea: QueryList<ElementRef>;

  constructor(
    public utilService: UtilService,
    private commandService: CommandService,
    public blackboardService: BlackboardService,
    private jsonLogService: JsonLogService,
    private store: Store,
    private vcrClassroom: VcrClassroomComponent,
    private renderer: Renderer2
  ) {
    this.listenStoreChanges();
    this.blackboardService.toggleBoard = this.toggleBlackboard.bind(this);
    this.blackboardService.turnOnPointer = this.turnOnPointer.bind(this);
    this.blackboardService.turnOffPointer = this.turnOffPointer.bind(this);
    this.blackboardService.setText = this.setText.bind(this);
  }

  public ngOnInit(): void {}

  public ngAfterViewInit(): void {
    this.initCanvas();
    this.registerDomEventListeners();
    this.blackboardService.init();
  }

  private listenStoreChanges(): void {
    this.store.select(LocalUserState).subscribe((user: LocalUser) => {
      this.localUser = user;
    });
    this.store.select(BlackboardState).subscribe((blackboardStateModel: BlackboardStateModel) => {
      this.blackboardStateModel = blackboardStateModel;
    });
    this.vcrClassroom.viewportTabActivateEmitter$.subscribe((state: ViewportTab) => {
      if (state === ViewportTab.BLACKBOARD && this.selectedTool === this.tools.TEXT) {
        this.focusTextArea();
      }
    });
  }

  public creatNewCoordinate(coordinates: Coordinates): Coordinates {
    return {
      x: coordinates.x,
      y: coordinates.y
    };
  }

  private registerDomEventListeners(): void {
    if (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT) {
      document.addEventListener("contextmenu", (event) => {
        if (event.ctrlKey) {
          event.preventDefault(); // Disable Ctrl + Click
        }
      });
      this.canvas.addEventListener('mousedown', this.onDrawStart);
      this.canvas.addEventListener('touchstart', this.onDrawStart);
      window.addEventListener('keydown', this.handleKeyDown.bind(this));
      window.addEventListener('keyup', this.handleKeyUp.bind(this));
    }
  }
  handleKeyDown = (event: KeyboardEvent) => {
    if (event.shiftKey) {
      this.isShiftDown = true;
    }
    if (event.ctrlKey){
      this.isCtrlDown=true;
    }
  };

  handleKeyUp = (event: KeyboardEvent) => {
    if (this.isPainting && this.isShiftDown) {
      this.lastShiftPoint = Object.assign({}, this.coordinates);
      this.addStepsArray(Object.assign({}, this.lastShiftPoint));
      this.drawLine(this.bbInfo);
    }
    this.isShiftDown = false;
    this.isCtrlDown=false;
    this.lastCtrlPoint=this.coordinates;
  };

  private initCanvas(): void {
    this.pointerElement = document.getElementById('bbPointer') as unknown as SVGSVGElement;
    const canvas = document.getElementById('blackCanvas') as HTMLCanvasElement;
    const context = canvas.getContext('2d');
    context.lineCap = 'round';
    context.strokeStyle = 'white';
    context.lineWidth = 2;
    this.canvas = canvas;
    this.context = context;
    this.blackboardService.initCanvas(canvas, context, this.pointerElement);
  }

  public toggleBlackboard(send: boolean = true): void {
    if (send) {
      this.blackboardService.sendToggle(!this.blackboardStateModel.isOn).subscribe();
    }
  }

  private colorSwitcher(): void {
    if (this.selectedTool === this.tools.ERASER) {
      this.cleanArrays();
      this.bbInfo.color = 'rgb(34,34,34)';
      this.bbInfo.lineWide = 10;
    } else {
      this.cleanArrays();
      this.bbInfo.color = 'white';
      this.bbInfo.lineWide = 2;
    }
  }

  private cleanArrays(): void {
    this.bbInfo.xyArray = [];
  }

  private toggleEraser(): void {
    if (this.selectedTool === this.tools.POINTER) {
      this.togglePointer();
    }
    if (this.selectedTool === this.tools.ERASER) {
      this.selectedTool = this.tools.DRAW_MODE;
      this.colorSwitcher();
      this.jsonLogService.writeJsonLogs(JsonLogAction.BB_ERASER,{state: 'OFF'}).subscribe();
    } else {
      this.selectedTool = this.tools.ERASER;
      this.cleanArrays();
      this.colorSwitcher();
      this.jsonLogService.writeJsonLogs(JsonLogAction.BB_ERASER,{state: 'ON'}).subscribe();
    }
  }

  private toggleText(): void {
    if (this.selectedTool === this.tools.POINTER) {
      this.togglePointer();
    }
    if (this.selectedTool === this.tools.TEXT) {
      this.selectedTool = this.tools.DRAW_MODE;
      this.colorSwitcher();
      document.getElementById('bbTextArea').removeEventListener('input', this.onTextInput);
    } else {
      this.focusTextArea();
      this.selectedTool = this.tools.TEXT;
      this.colorSwitcher();
      document.getElementById('bbTextArea').addEventListener('input', this.onTextInput);
    }
  }

  public focusTextArea(): void {
    if (this.bbTextArea && this.bbTextArea.length > 0) {
      const firstElement = this.bbTextArea.first.nativeElement;
      this.renderer.selectRootElement(firstElement).focus();
    }
  }

  private togglePointer(send: boolean = true): void {
    if (this.selectedTool === this.tools.POINTER) {
      this.turnOffPointer(send);
    } else {
      this.turnOnPointer(send);
    }
  }

  private turnOnPointer(send: boolean = true): void {
    if (this.localUser.accessType !== AccessType.STUDENT) {
      document.addEventListener('mousemove', this.onPointerMove);
      this.canvas.removeEventListener('mousedown', this.onDrawStart);
      this.canvas.removeEventListener('touchstart', this.onDrawStart);
    }
    this.selectedTool = this.tools.POINTER;
    this.pointer.isHidden = false;
    if (send) {
      this.blackboardService.sendTogglePointer(true).subscribe();
    }
  }

  private turnOffPointer(send: boolean = true): void {
    this.selectedTool = this.tools.DRAW_MODE;
    if (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT) {
      this.canvas.addEventListener('mousedown', this.onDrawStart);
      this.canvas.addEventListener('touchstart', this.onDrawStart);
    }
    this.colorSwitcher();
    document.removeEventListener('mousemove', this.onPointerMove);
    this.pointer.isHidden = true;
    if (send) {
      this.blackboardService.sendTogglePointer(false).subscribe();
    }
  }

  public selectBlackboardTool(toolNumber: number): void {
    switch (toolNumber) {
      case this.tools.ERASER: {
        this.toggleEraser();
        break;
      }
      case this.tools.TEXT: {
        this.toggleText();
        break;
      }
      case this.tools.POINTER: {
        this.togglePointer();
        break;
      }
    }
  }

  private drawLine(bbInfo: any): void {
    if (bbInfo.xyArray.length > 0) {
      for (let i = bbInfo.xyArray.length - 1; i < bbInfo.xyArray.length; i++) {
        this.context.beginPath();
        if (i > 0) {
          this.context.moveTo(bbInfo.xyArray[i - 1].x, bbInfo.xyArray[i - 1].y);
        }
        this.context.strokeStyle = bbInfo.color;
        this.context.lineWidth = bbInfo.lineWide;
        this.context.lineTo(bbInfo.xyArray[i].x, bbInfo.xyArray[i].y);
        this.context.stroke();
      }
    }
  }

  private fixLineAndDrawForRemoteUser(bbInfo: BbDrawInfo): void {
    for (let i = 0; i < bbInfo.xyArray.length; i++) {
      this.context.beginPath();
      if (i > 0) {
        this.context.moveTo(bbInfo.xyArray[i - 1].x, bbInfo.xyArray[i - 1].y);
      }
      this.context.strokeStyle = bbInfo.color;
      this.context.lineWidth = bbInfo.lineWide;
      this.context.lineTo(bbInfo.xyArray[i].x, bbInfo.xyArray[i].y);
      this.context.stroke();
      this.context.beginPath();
    }
  }

  private sendDrawPath(coordinate: BbDrawInfo): void {
    const drawSegmentModel = new BlackboardData();
    const coordinates = new BbDrawInfo();
    coordinates.xyArray = coordinate.xyArray;
    coordinates.color = coordinate.color;
    coordinates.lineWide = coordinate.lineWide;
    drawSegmentModel.bbDrawInfo = coordinates;
    drawSegmentModel.isPageReload = false;
    this.blackboardService.sendDraw(drawSegmentModel).subscribe();
  }

  private addStepsArray(stepCoordinate: Coordinates): void {
    this.bbInfo.xyArray.push(this.creatNewCoordinate(stepCoordinate));
  }

  private cleanCanvas(clean?: boolean): void {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.cleanArrays();
    this.text = '';
  }

  public clearEventHandler(): void {
    if (window.confirm('Are you sure you want to clear the blackboard?')) {
      this.cleanCanvas(true);
      this.blackboardService.sendClear().subscribe();
    }
  };

  private onDrawStart = (e: MouseEvent | TouchEvent) => {
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('touchmove', this.onMouseMove);
    document.addEventListener('mouseup', this.onDrawEnd);
    document.addEventListener('touchend', this.onDrawEnd);
    this.coordinates.x = (e as TouchEvent).changedTouches ?
      (e as TouchEvent).changedTouches[0].pageX :
      (e as MouseEvent).pageX;
    this.coordinates.y = (e as TouchEvent).changedTouches ?
      (e as TouchEvent).changedTouches[0].pageY :
      (e as MouseEvent).pageY;
    const canvasRect: Rect = this.canvas.getBoundingClientRect();
    this.coordinates.x -= canvasRect.x;
    this.coordinates.y -= canvasRect.y;
    if (this.isCtrlDown){
      this.addStepsArray(Object.assign({}, this.lastCtrlPoint));
      this.addStepsArray(this.coordinates);
      this.drawLine(this.bbInfo);
      this.lastCtrlPoint = Object.assign({}, this.coordinates);
    }
    this.lastShiftPoint = Object.assign({}, this.coordinates);
    this.isPainting = true;
    this.context.beginPath();
    this.addStepsArray(this.coordinates);
    this.drawLine(this.bbInfo);
    this.canvas.addEventListener('mouseout', this.onMouseOut);
    e.preventDefault();
  };

  private onMouseMove = (e: MouseEvent | TouchEvent) => {
    this.coordinates.x = (e as TouchEvent).changedTouches ?
      (e as TouchEvent).changedTouches[0].pageX :
      (e as MouseEvent).pageX;
    this.coordinates.y = (e as TouchEvent).changedTouches ?
      (e as TouchEvent).changedTouches[0].pageY :
      (e as MouseEvent).pageY;
    const canvasRect: Rect = this.canvas.getBoundingClientRect();
    this.coordinates.x -= canvasRect.x;
    this.coordinates.y -= canvasRect.y;
    if (this.isPainting) {
      if (this.isShiftDown) {
        this.lastShiftPoint = Object.assign({}, this.coordinates);
      }else if (this.isCtrlDown) {
        this.isPainting=false;
      }
      else {
        this.addStepsArray(this.coordinates);
        this.drawLine(this.bbInfo);
      }
    }
    e.preventDefault();
  };

  private onDrawEnd = () => {
    if (this.isShiftDown) {
      this.lastShiftPoint = Object.assign({}, this.coordinates);
      this.addStepsArray(Object.assign({}, this.lastShiftPoint));
    }
    this.fixLineAndDrawForRemoteUser(this.bbInfo);
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('touchmove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onDrawEnd);
    document.removeEventListener('touchend', this.onDrawEnd);
    this.canvas.removeEventListener('mouseout', this.onMouseOut);
    this.isPainting = false;
    this.sendDrawPath(this.bbInfo);
    this.cleanArrays();
  };

  private handleFastMouseMove(mouseX: number, mouseY: number): void {
    mouseX -= this.canvas.offsetLeft + 10;
    mouseY -= this.canvas.offsetTop + 50;
    if (mouseX > 800 || mouseY > 300 || mouseX < 0 || mouseY < 0) {
      this.coordinates.x = mouseX;
      this.coordinates.y = mouseY;
      this.bbInfo.xyArray.push(this.creatNewCoordinate(this.coordinates));
      this.drawLine(this.bbInfo);
    }
  }

  private onMouseOut = (e: MouseEvent) => {
    if (this.isShiftDown) {
      this.isPainting = false;
      this.sendDrawPath(this.bbInfo);
      this.cleanArrays();
    } else {
      this.handleFastMouseMove(e.clientX, e.clientY);
      this.isPainting = false;
      this.sendDrawPath(this.bbInfo);
      this.cleanArrays();
    }
  };

  private onPointerMove = (e: MouseEvent) => {
    const canvasRect: Rect = this.canvas.getBoundingClientRect();
    const pointerRect: Rect = this.pointerElement.getBoundingClientRect();
    const coordinates: Coordinates = {
      x: e.pageX - canvasRect.x - pointerRect.width / 2,
      y: e.pageY - canvasRect.y - pointerRect.height / 2
    };
    this.blackboardService.movePointer(coordinates);
  };

  private sendMessage(): void {
    clearTimeout(this.messageSendTimer);
    this.messageSendTimer = setTimeout(() => {
      this.blackboardService.sendText(this.text).subscribe();
    }, 500);
  }

  private setText(text: string): void {
    this.text = text;
  }

  private onTextInput = (e: InputEvent) => {
    this.blackboardService.alertTextBorderOut();
    this.sendMessage();
  };
}
